/**
 * Grupo matriz/filiais na tela do balancete.
 *
 * A rota `GET /empresas/{id}/grupo` (backend #1462) devolve, para qualquer
 * estabelecimento, todos os da mesma pessoa jurídica — pela raiz do CNPJ,
 * matriz primeiro. Aqui ficam as REGRAS DE APRESENTAÇÃO desse payload: o que
 * vale mostrar, como rotular cada membro, quem pode ser aberto.
 *
 * O que esta tela faz com o grupo: dizer ao contador ONDE ele está ("matriz +
 * 2 filiais") e deixá-lo trocar de estabelecimento sem voltar aos cards. O que
 * ela NÃO faz: somar nada. Consolidar o grupo é outra frente — na raiz
 * 18416559, 152 dos 172 lançamentos da filial estão replicados na matriz, e
 * "grupo = soma dos membros" daria o dobro.
 */

import { IEmpresaOption } from '../../../../hooks/useContaCilImportar';

export interface IGrupoEmpresasCobertura {
  lancamentos: number;
  lancamentos_de: string | null;
  lancamentos_ate: string | null;
  saldos_mes: number;
  saldos_de: string | null;
  saldos_ate: string | null;
  tem_plano: boolean;
}

export interface IGrupoEmpresasMembro {
  /** `tb_empresa.id` — o que a tela usa para selecionar. */
  empresa_id: number;
  /** Código do Domínio. NULO para linha do Acessórias (não tem `extract_*`). */
  external_id: number | null;
  external_id_origem: string;
  /** 'codi_emp' (Domínio) ou 'ID' (Acessórias). */
  fonte: string;
  cnpj: string;
  cnpj_raiz: string | null;
  /** '0001' na matriz. */
  ordem: string | null;
  papel: 'matriz' | 'filial' | null;
  nome: string | null;
  uf: string | null;
  cidade: string | null;
  /** Texto original: 'Ativa' | 'Inativa' | 'ForaDaDominio' | 'ForaDoAcessorias'. */
  ativa: string | null;
  grupo_de_empresas: string | null;
  regime: string;
  is_sn: boolean;
  duplicado_external_id: boolean;
  duplicado_cnpj: boolean;
  /** Nulo quando não há o que consultar (sem código do Domínio). Zeros = extraído e vazio. */
  cobertura: IGrupoEmpresasCobertura | null;
}

export interface IGrupoEmpresas {
  consultada_empresa_id: number;
  /** Quem REPRESENTA a consultada na lista (ela mesma, ou o canônico se é cadastro duplicado). */
  consultada_membro_id: number | null;
  cnpj_raiz: string | null;
  /** Nulo quando a matriz não é cliente ou quando há empate sem desempate. */
  matriz_empresa_id: number | null;
  total_membros: number;
  cobertura_incluida: boolean;
  membros: IGrupoEmpresasMembro[];
  avisos: string[];
}

export const ehAtiva = (m: IGrupoEmpresasMembro): boolean => (m.ativa ?? '').trim() === 'Ativa';

/**
 * Só vale mostrar a barra quando há o que mostrar: mais de um membro, OU uma
 * filial sozinha (a matriz não é cliente — o contador precisa saber que o que
 * ele vê é uma filial). Matriz sozinha é o caso normal e fica muda: a maioria
 * das 1.600 empresas da carteira não tem filial, e uma barra em todas seria
 * ruído.
 */
export const temGrupo = (g: IGrupoEmpresas | null | undefined): boolean =>
  !!g && (g.total_membros > 1 || g.membros.some(m => m.papel === 'filial'));

/** "Matriz" | "Filial 0003" — o que identifica o estabelecimento num relance. */
export const papelCurto = (m: IGrupoEmpresasMembro): string => {
  if (m.papel === 'matriz') return 'Matriz';
  if (m.papel === 'filial') return `Filial ${m.ordem ?? ''}`.trim();
  return 'Sem papel';
};

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * "matriz + 2 filiais (1 inativa)" — a frase do cabeçalho.
 *
 * Diz explicitamente quando a matriz NÃO é cliente: senão "1 filial" ao lado
 * de uma empresa lê como se a matriz estivesse em algum lugar da tela.
 */
export const resumoDoGrupo = (g: IGrupoEmpresas): string => {
  const filiais = g.membros.filter(m => m.papel === 'filial').length;
  const inativas = g.membros.filter(m => !ehAtiva(m)).length;
  const base =
    g.matriz_empresa_id != null
      ? `matriz${filiais ? ` + ${plural(filiais, 'filial', 'filiais')}` : ''}`
      : `${plural(filiais, 'filial', 'filiais')} · matriz não é cliente`;
  const ressalva = inativas ? ` (${plural(inativas, 'inativa', 'inativas')})` : '';
  return base + ressalva;
};

/**
 * O membro que representa a empresa selecionada. É `consultada_membro_id`, não
 * o id clicado: quando a selecionada é cadastro duplicado, quem está na lista é
 * o canônico — e é ele que tem que aparecer marcado.
 */
export const eAtual = (m: IGrupoEmpresasMembro, g: IGrupoEmpresas): boolean =>
  m.empresa_id === g.consultada_membro_id;

/**
 * Só abre quem tem código do Domínio: uma linha do Acessórias não tem
 * `extract_*`, e selecioná-la levaria a um balancete vazio com erro 422.
 *
 * Basta olhar `external_id`: o backend o devolve NULO exatamente quando não há
 * código do Domínio (fonte 'ID', ou codi_emp não numérico). Checar `fonte`
 * também seria uma segunda linha defendendo a mesma regra — o teste de mutação
 * mostrou que ela não mudava resultado nenhum.
 */
export const podeAbrir = (m: IGrupoEmpresasMembro): boolean => m.external_id != null;

/** A mesma forma que os cards produzem ao selecionar uma empresa. */
export const paraOpcao = (m: IGrupoEmpresasMembro): IEmpresaOption => ({
  value: m.empresa_id,
  label: m.nome ?? `Empresa ${m.empresa_id}`,
  external_id: String(m.external_id ?? ''),
});

/** 'YYYY-MM-DD' -> 'MM/YYYY'. Datas vêm em ISO da API. */
const mesAno = (iso: string | null): string | null =>
  iso && iso.length >= 7 ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : null;

const fmtInt = (n: number): string => n.toLocaleString('pt-BR');

/** " de 02/2024 a 09/2026" | " em 01/2026" (mesmo mês) | "" (sem datas). */
const rotuloPeriodo = (deIso: string | null, ateIso: string | null): string => {
  const de = mesAno(deIso);
  const ate = mesAno(ateIso);
  if (!de || !ate) return '';
  if (de === ate) return ` em ${de}`;
  return ` de ${de} a ${ate}`;
};

/**
 * Uma linha sobre o que existe extraído do membro — vai no hover do chip.
 *
 * Distingue os três estados que o dado tem de verdade: sem código no Domínio
 * (não há o que consultar), extraído e VAZIO (zeros — na raiz 30102178 as 3
 * filiais têm plano de contas e nenhum lançamento), e com movimento (quanto e
 * de quando a quando). Confundir "vazia" com "não extraída" é o erro que a
 * cobertura existe para evitar.
 */
export const coberturaCurta = (m: IGrupoEmpresasMembro): string => {
  const c = m.cobertura;
  if (!c) return m.fonte === 'codi_emp' ? 'cobertura não consultada' : 'sem código no Domínio';
  if (c.lancamentos === 0 && c.saldos_mes === 0) {
    return c.tem_plano ? 'plano de contas sem lançamentos extraídos' : 'nada extraído';
  }
  const partes: string[] = [];
  if (c.lancamentos > 0) {
    const periodo = rotuloPeriodo(c.lancamentos_de, c.lancamentos_ate);
    partes.push(`${fmtInt(c.lancamentos)} lançamentos${periodo}`);
  }
  if (c.saldos_mes > 0) partes.push(`${fmtInt(c.saldos_mes)} saldos mensais`);
  return partes.join(' · ');
};

/** Marca discreta no chip: o cadastro tem duplicidade que o backend não pôde desempatar. */
export const temRessalva = (m: IGrupoEmpresasMembro): boolean =>
  m.duplicado_cnpj || m.duplicado_external_id;

/**
 * SELEÇÃO DE ESTABELECIMENTOS PARA SOMAR (pedido do usuário, 01/10/2026).
 *
 * A soma é SIMPLES — nada é eliminado —, e por isso a tela é obrigada a exibir
 * os `avisos` que o backend manda junto. Ver `balancete_consolidado.py`: a
 * folha da 970 está lançada nos dois livros e a 856 tem 5,7 mi em contas que
 * representam a própria filial.
 */

/** Pode entrar na soma: sem código do Domínio não há balancete para somar. */
export const podeSomar = (m: IGrupoEmpresasMembro): boolean => m.external_id != null;

/**
 * A seleção inicial: só a empresa aberta.
 *
 * NÃO começa com o grupo todo marcado de propósito — o número somado é
 * diferente do balancete de uma empresa, e ninguém deve receber um total
 * consolidado sem ter pedido.
 */
export const selecaoInicial = (g: IGrupoEmpresas): number[] =>
  g.consultada_membro_id != null ? [g.consultada_membro_id] : [];

/**
 * Marca/desmarca um estabelecimento.
 *
 * A empresa ABERTA nunca sai da seleção: ela é a dona da tela (o balancete é
 * buscado pelo id dela), e desmarcá-la deixaria a tela sem base — além de o
 * backend reinjetá-la de qualquer forma, o que faria a caixinha mentir.
 */
export const alternarSelecao = (
  selecionados: number[],
  empresaId: number,
  g: IGrupoEmpresas,
): number[] => {
  if (empresaId === g.consultada_membro_id) return selecionados;
  const fora = selecionados.filter(id => id !== empresaId);
  return fora.length === selecionados.length ? [...selecionados, empresaId] : fora;
};

/** `true` quando há mais de um estabelecimento marcado — é o que liga a soma. */
export const estaConsolidado = (selecionados: number[]): boolean => selecionados.length > 1;

/**
 * Os ids que vão na query `?empresas=`, ordenados para a chave do cache do
 * react-query não mudar só porque o usuário clicou em outra ordem.
 */
export const paramEmpresas = (selecionados: number[]): string =>
  [...selecionados].sort((a, b) => a - b).join(',');

/**
 * Frase do cabeçalho quando a soma está ligada. Diz QUANTOS estabelecimentos
 * entraram — sem isso o contador não sabe se o número é de um ou de três.
 */
export const resumoDaSelecao = (selecionados: number[]): string =>
  `Somando ${selecionados.length} estabelecimentos`;

/**
 * O `title` da caixinha de somar — três casos, e dizer o motivo importa: uma
 * caixinha desabilitada sem explicação parece defeito da tela.
 */
export const dicaDeSomar = (podeSomarMembro: boolean, ehAberta: boolean): string => {
  if (!podeSomarMembro) return 'Sem código do Domínio: não há balancete para somar';
  if (ehAberta) return 'A empresa aberta entra sempre na soma';
  return 'Marcar para somar ao balancete';
};
